import type { ReactNode } from "react"

import { IconButton } from "../ui/button"
import { Counter } from "../ui/counter"
import type { ShellLayout } from "./layout"
import styles from "./topbar.module.css"

/**
 * Шапка кабинета, 46 px.
 *
 * ЧТО В НЕЙ ОБЯЗАНО БЫТЬ ВСЕГДА (ТЗ §4.6): название раздела, филиал и свежесть данных.
 * Последнее — не техническая подробность: «Данные на 13:47» это то, по чему администратор
 * решает, верить ли пустому окну в сетке или перезагрузить страницу перед тем, как
 * пообещать клиенту время.
 *
 * ПОЧЕМУ У ПОИСКА И «НОВОЙ ЗАПИСИ» СТОИТ ПРИЧИНА, А НЕ ИХ ПРОСТО НЕТ. Экранов под них
 * в этапе 0 ещё нет. По ТЗ §3.6 прячут то, чего у человека не будет никогда, а то, что
 * появится или выдаётся по праву, показывают выключенным с пояснением. Спрятать их значит
 * потерять и проверку плотности 1366×768, ради которой шапка и рисовалась, и честность:
 * администратор, не нашедший поиска, будет искать его в настройках.
 */
export function Topbar(props: {
  readonly layout: ShellLayout
  readonly title: string
  /** Вторая строка телефона: «Ср, 16 сентября · Барбершоп на Ленина». */
  readonly subtitle?: string | undefined
  readonly freshness: string
  readonly branch: ReactNode
  /** Сколько событий требуют действия. undefined — ещё не знаем. */
  readonly notifications?: number | undefined
  readonly notificationsDeniedReason?: string | undefined
  /** Открыть центр уведомлений. Пусто — колокольчик выключен с объяснением. */
  readonly onOpenNotifications?: (() => void) | undefined
}): ReactNode {
  const phone = props.layout === "phone"

  const unread = props.notifications ?? 0
  const bell = (
    <IconButton
      // Число из счётчика голосом не звучит: aria-label заменяет собой содержимое кнопки.
      // Поэтому оно повторено словами здесь — иначе счётчик виден, но не слышен.
      label={unread === 0 ? "Центр уведомлений" : `Центр уведомлений, ${unread} требуют действия`}
      size={phone ? "lg" : "md"}
      framed
      deniedReason={props.notificationsDeniedReason}
      {...(props.onOpenNotifications === undefined
        ? {}
        : { onClick: props.onOpenNotifications })}
      badge={
        unread === 0 ? null : (
          <Counter value={unread} label="событий требуют действия" tone="attention" corner />
        )
      }
      icon={
        <svg
          className={styles.bell}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
        </svg>
      }
    />
  )

  if (phone) {
    return (
      <header className={styles.phoneBar}>
        <div className={styles.phoneTitles}>
          <h1 className={styles.phoneTitle}>{props.title}</h1>
          <p className={styles.phoneSubtitle}>{props.subtitle ?? props.freshness}</p>
        </div>
        {bell}
      </header>
    )
  }

  return (
    <header className={styles.bar}>
      <h1 className={styles.title}>{props.title}</h1>

      {props.layout === "desktop" ? (
        <label className={styles.search}>
          <span className="plnr-visually-hidden">Поиск по клиентам и записям</span>
          <svg
            className={styles.searchIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="6.5" />
            <path d="m20 20-3.8-3.8" />
          </svg>
          <input
            className={styles.searchInput}
            type="search"
            placeholder="Клиент, запись, телефон…"
            disabled
            title="Общий поиск появится вместе с разделом «Клиенты»"
          />
        </label>
      ) : null}

      <div className={styles.right}>
        <span className={styles.freshness}>{props.freshness}</span>
        {props.branch}
        {bell}
        {/*
          * «+ Новая запись» в шапке БОЛЬШЕ НЕТ. Она никуда не вела: нажатие отвечало
          * «появится вместе с карточкой записи», хотя запись в журнале работает и кнопка
          * там своя — на выбранном дне, мастере и времени. Две одинаковые кнопки, из
          * которых рабочая только нижняя, учат не верить верхней (5-А1, поправка к 2-2.5).
          */}
      </div>
    </header>
  )
}
